import { type UseQueryStatesKeysMap, useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

/**
 * NOTE
 * //////////////////////////////////////////////////////////////
 * 1. URL だけ即書き換え
 * navigate('/projects?q=foo') / <Link> をクリックすると
 * → ブラウザが history.pushState を実行し、アドレスバーだけ先に変わる。
 * 2. 旧ページが「新 URL」で 1 回だけ再レンダー
  Router は まだ残っている PageA に新しい location を流し込み、
  差分計算のため一度だけ描画させる（ここが止められない仕様）。
 * 3. 旧ページが default 値を描こうとする
 * useQueryState など URL 依存フックは まだパラメータが反映されていない ので
 * → 空文字や初期値で UI を描画し始める。
 * 4. 差分計算が終わり新ページをマウント
 * React が PageA をアンマウントし PageB をマウント。
 * 旧ページで見えた初期値と新ページの本来の画面が入れ替わる瞬間が“ちらつき”。
 * 5. ユーザーには「一瞬、空／初期値→完成画面」が見える
 * ユーザーには「一瞬、空／初期値→完成画面」が見える
 * //////////////////////////////////////////////////////////////  
 * 上記の理由から、debounced を使用している。
 */
export const useDebouncedQueryStates = <KeyMap extends UseQueryStatesKeysMap>(
  keyMap: KeyMap,
) => {
  const [searchQueryStates, setSearchQueryStates] = useQueryStates(keyMap);

  const [debouncedSearchQueryStates, setDebouncedSearchQueryStates] =
    useState(searchQueryStates);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQueryStates(searchQueryStates);
    }, 100);

    return () => clearTimeout(timer);
  }, [searchQueryStates]);

  return {
    searchQueryStates: debouncedSearchQueryStates,
    setSearchQueryStates,
  };
};
